/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchBailRight,
  fetchBailRightByAssignedProsecutor,
  fetchBailRightByRole,
} from "services/bailRightService";
import { BailRight, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";
import { LatestAppointmentDaysLeft } from "../../components/LatestAppointmentDaysLeft";
import { ITEMS_PER_PAGE } from "utils/constant";

const TAB_KEYS = {
  all: "all",
  newRequest: "new_request",
  onHearing: "on_hearing",
  closed: "closed",
} as const;

const BailRightList: React.FC = () => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);

  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);

  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);

  const isChildWomenCoordinator = hasRole(loginData?.roles, [
    Roles.childWomenCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : isVariousDirector
      ? Roles.variousDirector
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isEconomicCoordinator
          ? Roles.economicCoordinator
          : isEconomicDirector
            ? Roles.economicDirector
            : isEconomicRegistrar
              ? Roles.economicRegistrar
              : isCorruptionCoordinator
                ? Roles.corruptionCoordinator
                : isCorruptionDirector
                  ? Roles.corruptionDirector
                  : isCorruptionRegistrar
                    ? Roles.corruptionRegistrar
                    : isChildWomenCoordinator
                      ? Roles.childWomenCoordinator
                      : null;

  const tabs = [t("all"), t("new_request"), "On Hearing", t("status_closed")];
  const [activeTab, setActiveTab] = useState(t("all"));
  const [bailRights, setBailRights] = useState<BailRight[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);

  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const fetchData = () => {
    setLoading(true);

    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchBailRightByRole(
            loginData?.admin_unit || "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isProsecutor
          ? fetchBailRightByAssignedProsecutor(loginData?.user_id || "")
          : selectedAdminRole
            ? fetchBailRightByRole(
                loginData?.admin_unit || "",
                selectedAdminRole,
              )
            : fetchBailRight();
    api
      .then((data) => {
        setBailRights(
          data?.sort((a, b) => {
            const dateA = new Date(a.created_at || "").getTime();
            const dateB = new Date(b.created_at || "").getTime();
            return dateB - dateA;
          }) || [],
        );
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  const getStatusMatchForTab = (bail: any) => {
    const status = bail?.status?.toString() || "";
    const hasFinalDecision = (bail?.hearing_decisions || []).some(
      (h: any) => h?.if_final_decision_given === true,
    );

    if (activeTab === t(TAB_KEYS.all)) return true;
    if (activeTab === t(TAB_KEYS.newRequest)) {
      return status === "forwarded_to_coordinator" && !bail?.prosecutor_summary;
    }
    if (activeTab === "On Hearing") {
      return (
        status === "assigned_to_prosecutor" &&
        !!bail?.reference_number &&
        !hasFinalDecision
      );
    }
    if (activeTab === t("status_closed")) {
      return (
        hasFinalDecision ||
        status === "bail_rejected" ||
        status === "bail_granted"
      );
    }

    return true;
  };

  const searchTerm = search.trim().toLowerCase();
  const filtered = bailRights.filter((bail) => {
    const incident_registration_number =
      bail?.incident_registration_number?.toString()?.toLowerCase() || "";
    const crime_registration_number =
      bail?.crime_registration_number?.toString()?.toLowerCase() || "";
    const status = bail?.status?.toString()?.toLowerCase() || "";
    const court_registration_number =
      bail?.court_registration_number?.toString()?.toLowerCase() || "";
    const court_name = bail?.court_name?.toString()?.toLowerCase() || "";
    const suspect_fullname =
      bail?.suspect_fullname?.toString()?.toLowerCase() || "";
    const bail_type = bail?.bail_type?.toString()?.toLowerCase() || "";

    const matchesText =
      incident_registration_number.includes(searchTerm) ||
      crime_registration_number.includes(searchTerm) ||
      status.includes(searchTerm) ||
      court_registration_number.includes(searchTerm) ||
      court_name.includes(searchTerm) ||
      suspect_fullname.includes(searchTerm) ||
      bail_type.includes(searchTerm);

    return getStatusMatchForTab(bail) && matchesText;
  });

  const [currentPage, setCurrentPage] = useState(1);

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedData = filtered.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("bail_right")}
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={
          <>
            {isRegistrar && (
              <button
                onClick={() => navigate("/bail-right-request")}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                {t("register_for_bail_right")}
              </button>
            )}
          </>
        }
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_bail_rights")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("bail_right_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">{t("suspect")}</th>
                  <th className="p-3 text-left">{t("court_reg_no")}</th>
                  <th className="p-3 text-left">{t("court_name")}</th>
                  <th className="p-3 text-left">{t("bail_type")}</th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((bail: any, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {bail?.suspect_fullname ||
                        bail?.suspect_info?.first_name +
                          " " +
                          bail?.suspect_info?.middle_name +
                          " " +
                          bail?.suspect_info?.last_name}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {bail?.court_registration_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {bail.court_name}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {bail?.bail_type && t(`bail_type_${bail?.bail_type}`)}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge bail={bail} t={t} />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins] flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/bail-right/" + bail?.uu_id + "/");
                        }}
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("view")}
                      </button>
                      <LatestAppointmentDaysLeft
                        hearings={bail?.hearing_decisions as any}
                        t={t}
                      />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_bail_right_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("prev")}
            </button>
            <span className="px-2">
              {t("page")} {currentPage} {t("of")} {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const BAIL_STAGE_STYLES: Record<string, string> = {
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  teal: "bg-teal-100 text-teal-800 border-teal-200",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  gray: "bg-gray-100 text-gray-800 border-gray-200",
};

// Derive the real lifecycle stage from the bail record. The raw `status`
// stays "assigned_to_prosecutor" both before AND during hearings, so we look
// at the reference number (set by the registrar to start hearings) and the
// hearing decisions (a final decision means the court has ruled).
const deriveBailStage = (
  bail: any,
  t: any,
): { label: string; color: string; icon: string } => {
  const status = (bail?.status || "").toString();
  const hearings = bail?.hearing_decisions || [];
  const finalDecision = hearings.find(
    (h: any) => h?.if_final_decision_given === true,
  );

  // Court has ruled (final decision recorded, or closed status).
  if (finalDecision || status === "bail_granted" || status === "bail_rejected") {
    const action = finalDecision?.decision_action;
    if (action === "grant" || status === "bail_granted")
      return {
        label: t("status_bail_granted", { defaultValue: "Bail Granted" }),
        color: "green",
        icon: "✅",
      };
    if (action === "not_grant" || status === "bail_rejected")
      return {
        label: t("status_bail_rejected", { defaultValue: "Bail Not Granted" }),
        color: "red",
        icon: "⛔",
      };
    return {
      label: t("court_decision_given", { defaultValue: "Court Decision Given" }),
      color: "teal",
      icon: "⚖️",
    };
  }

  // Assigned to a prosecutor WITH a reference number → hearings underway.
  if (status === "assigned_to_prosecutor" && bail?.reference_number)
    return {
      label: t("on_hearing", { defaultValue: "On Hearing" }),
      color: "indigo",
      icon: "⚖️",
    };

  // Assigned to a prosecutor, no reference number yet → initial review.
  if (status === "assigned_to_prosecutor")
    return {
      label: t("status_assigned_to_prosecutor", {
        defaultValue: "Assigned to Prosecutor",
      }),
      color: "red",
      icon: "👤",
    };

  // Prosecutor returned a recommendation, waiting on the registrar.
  if (status === "forwarded_to_registrar")
    return {
      label: t("status_forwarded_to_registrar", {
        defaultValue: "With Registrar",
      }),
      color: "blue",
      icon: "🗂️",
    };

  // Back with the coordinator: a new request, or a prosecutor recommendation
  // awaiting approval (distinguished by the presence of a prosecutor summary).
  if (status === "forwarded_to_coordinator")
    return bail?.prosecutor_summary
      ? {
          label: t("awaiting_coordinator_approval", {
            defaultValue: "Awaiting Coordinator Approval",
          }),
          color: "amber",
          icon: "⏳",
        }
      : {
          label: t("status_forwarded_to_coordinator", {
            defaultValue: "New Request",
          }),
          color: "purple",
          icon: "🆕",
        };

  return {
    label: t(`status_${status}`, { defaultValue: status || t("na") }),
    color: "gray",
    icon: "",
  };
};

const StatusBadge: React.FC<{
  bail: any;
  t: any;
}> = ({ bail, t }) => {
  const { label, color, icon } = deriveBailStage(bail, t);
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${
        BAIL_STAGE_STYLES[color] || BAIL_STAGE_STYLES.gray
      }`}
    >
      {icon && <span>{icon}</span>}
      {label}
    </span>
  );
};

export default BailRightList;
